/**
 * Observations State
 *
 * NGXS state for managing observations data with pagination.
 * Stores observations loaded from the partners/{partnerId}/observations subcollection
 * and provides selectors for accessing observation data throughout the application.
 */

import { Injectable } from '@angular/core';
import {
  State,
  Action,
  StateContext,
  Selector,
  NgxsOnInit,
  Store,
} from '@ngxs/store';
import { Observation } from 'src/app/models/schema';
import {
  InitializeObservations,
  InitializeObservationsSuccess,
  InitializeObservationsFailure,
  LoadNextBatch,
  LoadNextBatchSuccess,
  LoadNextBatchFailure,
  SetObservationsFilters,
  SetObservationsSort,
  AddObservation,
  AddObservationSuccess,
  AddObservationFailure,
  UpdateObservationsFromListener,
  ClearObservations,
  ClearObservationsError,
} from './observations.actions';
import { ActiveTeamState } from 'src/app/rxjs-store/account/active-team';
import { AuthState } from 'src/app/store/auth';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * Observations State Model
 *
 * State shape for the observations store
 */
export interface ObservationsStateModel {
  /** Array of observation entries */
  observations: Observation[];
  /** Whether observations are currently loading */
  loading: boolean;
  /** Whether the store has been initialized */
  initialized: boolean;
  /** Error message if an operation failed */
  error: string | null;
  /** Whether there are more observations to load */
  hasMore: boolean;
  /** Current page number (for tracking pagination) */
  currentPage: number;
  /** Current partner ID (for tracking which team's observations are loaded) */
  partnerId: string | null;
  /** Current filter criteria */
  filters: any;
  /** Current sort field */
  sortField: string;
  /** Current sort direction */
  sortDirection: 'asc' | 'desc';
}

/**
 * Default state values
 */
const defaults: ObservationsStateModel = {
  observations: [],
  loading: false,
  initialized: false,
  error: null,
  hasMore: true,
  currentPage: 0,
  partnerId: null,
  filters: {},
  sortField: 'createdAt',
  sortDirection: 'desc',
};

/**
 * Observations State
 *
 * Manages observations data in the NGXS store.
 * Automatically resets when the active team changes or when user signs out.
 */
@State<ObservationsStateModel>({
  name: 'observations',
  defaults,
})
@Injectable()
export class ObservationsState implements NgxsOnInit {
  constructor(private store: Store) {}

  ngxsOnInit(ctx: StateContext<ObservationsStateModel>): void {
    let previousAuthState: boolean | null = null;

    // Subscribe to auth state changes - clear on sign out
    this.store
      .select(AuthState.isAuthenticated)
      .pipe(distinctUntilChanged())
      .subscribe((isAuthenticated: boolean) => {
        // Only clear if we're transitioning from authenticated to unauthenticated
        // (not on initial load where auth state might not be hydrated yet)
        if (previousAuthState === true && !isAuthenticated) {
          ctx.setState(defaults);
          console.log('[ObservationsState] State cleared - user signed out');
        }

        // Track previous auth state
        previousAuthState = isAuthenticated;
      });

    // Subscribe to active team changes - reset state when team changes
    this.store
      .select(ActiveTeamState.activeTeamId)
      .pipe(distinctUntilChanged())
      .subscribe((activeTeamId) => {
        const currentState = ctx.getState();
        const currentPartnerId = currentState.partnerId;

        // Only clear if the activeTeamId has genuinely changed AND the state was previously loaded
        if (activeTeamId !== currentPartnerId && currentState.initialized) {
          ctx.setState(defaults);
          console.log(
            `[ObservationsState] Active team changed from ${currentPartnerId} to ${activeTeamId} - clearing observations`
          );
        }
      });
  }

  // ========================================
  // Selectors
  // ========================================

  /**
   * Select the entire observations state
   */
  @Selector()
  static getState(state: ObservationsStateModel): ObservationsStateModel {
    return state;
  }

  /**
   * Select all observations
   */
  @Selector()
  static observations(state: ObservationsStateModel): Observation[] {
    return state.observations;
  }

  /**
   * Select observations sorted by creation date (most recent first)
   */
  @Selector()
  static observationsSorted(state: ObservationsStateModel): Observation[] {
    return [...state.observations].sort((a, b) => {
      const aTime = a.createdAt || 0;
      const bTime = b.createdAt || 0;
      return bTime - aTime;
    });
  }

  /**
   * Select loading state
   */
  @Selector()
  static loading(state: ObservationsStateModel): boolean {
    return state.loading;
  }

  /**
   * Select initialized state
   */
  @Selector()
  static initialized(state: ObservationsStateModel): boolean {
    return state.initialized;
  }

  /**
   * Select error state
   */
  @Selector()
  static error(state: ObservationsStateModel): string | null {
    return state.error;
  }

  /**
   * Select hasMore pagination state
   */
  @Selector()
  static hasMore(state: ObservationsStateModel): boolean {
    return state.hasMore;
  }

  /**
   * Select current partner ID
   */
  @Selector()
  static partnerId(state: ObservationsStateModel): string | null {
    return state.partnerId;
  }

  /**
   * Select current filters
   */
  @Selector()
  static filters(state: ObservationsStateModel): any {
    return state.filters;
  }

  /**
   * Select current sort configuration
   */
  @Selector()
  static sort(state: ObservationsStateModel): {
    field: string;
    direction: 'asc' | 'desc';
  } {
    return {
      field: state.sortField,
      direction: state.sortDirection,
    };
  }

  // ========================================
  // Actions
  // ========================================

  /**
   * Initialize observations - load first batch
   */
  @Action(InitializeObservations)
  async initializeObservations(
    ctx: StateContext<ObservationsStateModel>,
    action: InitializeObservations
  ) {
    ctx.patchState({
      loading: true,
      error: null,
      partnerId: action.partnerId,
    });
  }

  /**
   * Handle successful initialization
   */
  @Action(InitializeObservationsSuccess)
  initializeObservationsSuccess(
    ctx: StateContext<ObservationsStateModel>,
    action: InitializeObservationsSuccess
  ) {
    ctx.patchState({
      observations: action.observations,
      hasMore: action.hasMore,
      loading: false,
      initialized: true,
      currentPage: 1,
      error: null,
    });
  }

  /**
   * Handle failed initialization
   */
  @Action(InitializeObservationsFailure)
  initializeObservationsFailure(
    ctx: StateContext<ObservationsStateModel>,
    action: InitializeObservationsFailure
  ) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Load next batch of observations
   */
  @Action(LoadNextBatch)
  async loadNextBatch(ctx: StateContext<ObservationsStateModel>) {
    ctx.patchState({
      loading: true,
      error: null,
    });
  }

  /**
   * Handle successful next batch load
   */
  @Action(LoadNextBatchSuccess)
  loadNextBatchSuccess(
    ctx: StateContext<ObservationsStateModel>,
    action: LoadNextBatchSuccess
  ) {
    const state = ctx.getState();
    ctx.patchState({
      observations: [...state.observations, ...action.observations],
      hasMore: action.hasMore,
      loading: false,
      currentPage: state.currentPage + 1,
      error: null,
    });
  }

  /**
   * Handle failed next batch load
   */
  @Action(LoadNextBatchFailure)
  loadNextBatchFailure(
    ctx: StateContext<ObservationsStateModel>,
    action: LoadNextBatchFailure
  ) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Set filter criteria
   */
  @Action(SetObservationsFilters)
  setFilters(
    ctx: StateContext<ObservationsStateModel>,
    action: SetObservationsFilters
  ) {
    ctx.patchState({
      filters: action.filters,
    });
  }

  /**
   * Set sort criteria
   */
  @Action(SetObservationsSort)
  setSort(
    ctx: StateContext<ObservationsStateModel>,
    action: SetObservationsSort
  ) {
    ctx.patchState({
      sortField: action.sortField,
      sortDirection: action.sortDirection,
    });
  }

  /**
   * Add a new observation
   */
  @Action(AddObservation)
  async addObservation(
    ctx: StateContext<ObservationsStateModel>,
    action: AddObservation
  ) {
    ctx.patchState({
      loading: true,
      error: null,
    });
  }

  /**
   * Handle successful observation add
   */
  @Action(AddObservationSuccess)
  addObservationSuccess(
    ctx: StateContext<ObservationsStateModel>,
    action: AddObservationSuccess
  ) {
    const state = ctx.getState();
    ctx.patchState({
      observations: [action.observation, ...state.observations],
      loading: false,
      error: null,
    });
  }

  /**
   * Handle failed observation add
   */
  @Action(AddObservationFailure)
  addObservationFailure(
    ctx: StateContext<ObservationsStateModel>,
    action: AddObservationFailure
  ) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Update observations from real-time listener
   */
  @Action(UpdateObservationsFromListener)
  updateObservationsFromListener(
    ctx: StateContext<ObservationsStateModel>,
    action: UpdateObservationsFromListener
  ) {
    ctx.patchState({
      observations: action.observations,
    });
  }

  /**
   * Clear all observations data
   */
  @Action(ClearObservations)
  clearObservations(ctx: StateContext<ObservationsStateModel>) {
    ctx.setState(defaults);
  }

  /**
   * Clear error state
   */
  @Action(ClearObservationsError)
  clearError(ctx: StateContext<ObservationsStateModel>) {
    ctx.patchState({
      error: null,
    });
  }
}
